import { NavBar, Drawer as ShellDrawer } from '@sede-enterprise/shell-ds-react-framework';

import { buildPalette } from '@eos/theme/palette';
import styled from 'styled-components';

const adminButtonPalette = buildPalette({
  light: {
    bg: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    shadow: '0 1px 3px rgba(71, 85, 105, 0.25)',
    shadowHover: '0 2px 6px rgba(71, 85, 105, 0.35)',
  },
  dark: {
    bg: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
    shadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowHover: '0 2px 6px rgba(0, 0, 0, 0.4)',
  },
});

const layoutPalette = buildPalette({
  light: {
    tabColor: '#757575',
    activeTabColor: '#404040',
    layoutBg: 'rgb(242, 242, 242)',
    headerBg: '#ffffff',
    pectenAreaBg: 'rgba(255, 255, 255, 0.1)',
    glass: `
      background: rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.18);
      box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.17);
    `,
  },
  dark: {
    tabColor: 'rgba(255, 255, 255, 0.57)',
    activeTabColor: '#FFFFFF',
    layoutBg: '#1C1C1C',
    headerBg: '#2C2C2C',
    pectenAreaBg: 'rgba(0, 0, 0, 0.05)',
    glass: `
      background: rgba(20, 20, 20, 0.25);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
    `,
  },
});

export const EOSLayoutContainer = styled.div`
  position: relative;
  font-family: 'system-ui', sans-serif;
  background-color: ${layoutPalette('layoutBg')};
  color: ${(prop) => prop.color};
  height: 100vh;

  .shell-tabs .shell-tabs-tab,
  .shell-menu-label {
    color: ${layoutPalette('tabColor')} !important;
  }

  .eos-menu-label-with-dot {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .eos-forecast-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-top: -10px;
    background-color: #2e8b57;
    box-shadow: 0 0 0 1px rgba(46, 139, 87, 0.25);
  }

  .shell-tabs .shell-tabs-tab-active,
  .shell-menu-item-selected > .shell-menu-label {
    color: ${layoutPalette('activeTabColor')} !important;
  }

  .shell-tabs .shell-tabs-tab-active .shell-tabs-tab-btn,
  .shell-tabs .shell-tabs-tab-active .shell-tabs-tab-btn span {
    font-weight: 600;
  }

  div:has(.shell-nav-bar) {
    height: 72px;
    background-color: ${layoutPalette('headerBg')};

    .shell-nav-bar {
      padding: unset;
      gap: 5px;
    }

    .shell-menu-item {
      padding: 25px 10px;
      font-size: 14px;
      font-weight: 600;
    }

    .shell-menu-label {
      display: flex;
      flex-direction: row-reverse;
      gap: 2px;
      .count {
        min-width: 15px;
        min-height: 15px;
        span {
          font-size: 10px;
        }
      }
    }

    .shell-menu-horizontal .shell-menu-item.shell-menu-item-selected {
      background: unset;
      ::after {
        height: 4px;
        bottom: unset;
        top: 0;
        background-color: #007acc; /* EOS brand color */
      }
    }
  }

  .shell-tabs .shell-tabs-tab-btn:focus {
    border: unset;
  }

  .eos-pending-count {
    background-color: #ff6b35; /* EOS warning color */
    color: white;
    height: 22px;
    width: auto;
    border-radius: 10px;
    text-align: center;
    margin-left: 3px;
    padding: 1px 5px;
    font-size: 10px;
    line-height: 10px;
  }

  div:has(.shell-menu-horizontal) {
    width: 100%;
  }

  .shell-menu-horizontal {
    width: 100%;
  }

  .eos-notification-count {
    position: absolute;
    top: 0;
    left: 20px;
  }

  .eos-system-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
  }

  .eos-header-actions {
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .eos-center-component {
    display: flex;
    align-items: center;
    gap: 16px;
  }
`;

export const EOSMidSection = styled.section`
  display: flex;
  flex: 0;
  min-height: calc(100vh - 76px);
  position: relative;
`;

export const EOSContentContainer = styled.section`
  flex: 1;
  box-sizing: border-box;
  width: 100%;
  transition: margin-left 0.5s;
`;

export const EOSNotificationDrawer = styled(ShellDrawer)`
  .shell-drawer-content {
    scrollbar-width: thin;
  }

  .shell-drawer-content > div {
    padding: 0 20px !important;
  }

  .eos-notification-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #e0e0e0;
  }

  .eos-notification-item {
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    .notification-title {
      font-weight: 600;
      margin-bottom: 4px;
    }

    .notification-message {
      color: #757575;
      font-size: 14px;
      margin-bottom: 4px;
    }

    .notification-timestamp {
      color: #9e9e9e;
      font-size: 12px;
    }

    &.unread {
      background-color: rgba(0, 122, 204, 0.05);
      border-left: 3px solid #007acc;
      padding-left: 9px;
    }
  }
`;

export const EOSHeaderIconWrapper = styled.div`
  svg {
    height: 27px;
    width: 27px;
    margin-top: 4px;
  }

  position: relative;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

export const EOSSwitchAppButton = styled.div`
  .eos-switch-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 6px;
    background-color: transparent;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: rgba(0, 122, 204, 0.1);
      border-color: #007acc;
    }

    .switch-icon {
      width: 16px;
      height: 16px;
    }

    .switch-text {
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

export const GlassEffect = layoutPalette('glass');

export const MainContainer = styled.div`
  box-shadow:
    0 2px 5px rgb(0 0 0 / 10%),
    0 0 2px rgb(0 0 0 / 10%);
  position: sticky;
  top: 0;
  z-index: 9;
`;

export const NavBarStyled = styled(NavBar)`
  box-shadow:
    0 2px 5px rgb(0 0 0 / 10%),
    0 0 2px rgb(0 0 0 / 10%);

  ${GlassEffect}

  position: sticky;
  top: 0;
  z-index: 999;
  min-height: 64px;

  .environment-wrapper {
    color: rgba(54, 61, 68, 1);
    font-weight: 600;
    font-size: 17px;
    background-color: #fbce07;
    padding: 10px 15px;
    border-radius: 15px;
    align-self: center;
  }
  .shell-drawer-content {
    padding: 0;
  }
`;

export const PectenWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${layoutPalette('pectenAreaBg')};

    .home-icon {
      transition: transform 0.2s ease;

      &:hover {
        transform: scale(1.05);
      }
    }
  }
`;

export const EOSHeader = styled.div<{ flexDirection: string }>`
  display: flex;
  flex-direction: ${(props) => props.flexDirection};
`;

export const AvatarArea = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 20px;

  .pointer {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
`;

export const EOSUserRole = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: rgba(0, 122, 204, 0.12);
  color: #007acc;
  white-space: nowrap;
`;

export const EOSAdminButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: ${adminButtonPalette('bg')};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: ${adminButtonPalette('shadow')};
  transition: all 0.2s ease;

  svg {
    width: 22px;
    height: 22px;
    fill: #ffffff;
  }

  &:hover {
    filter: brightness(1.06);
    box-shadow: ${adminButtonPalette('shadowHover')};
  }
`;
